/**
 * Map a single registration to a flat record for export (all possible keys).
 * Used by CSV/XLSX/PDF export with column selection.
 * @param {object} reg - Registration row (with student, lead, agent_sharing_links)
 * @returns {Record<string, string>}
 */
export function getRegistrationExportRow(reg) {
  const hasAccount = Boolean(reg.student_id)
  const name = (reg.student?.full_name ?? reg.lead?.full_name ?? reg.student_name) || ''
  const email = (reg.student?.email ?? reg.lead?.email ?? reg.student_email) || ''
  const phone = (reg.student?.phone ?? reg.lead?.phone ?? reg.student_phone) || ''
  const registered_date = reg.created_at ? new Date(reg.created_at).toLocaleDateString() : ''
  const status = reg.status ?? 'registered'
  const payment = reg.payment_status ?? ''
  const account = hasAccount ? 'Account Created' : 'No Account'
  const link = Array.isArray(reg.agent_sharing_links) ? reg.agent_sharing_links[0] : reg.agent_sharing_links
  const slug = link?.slug ?? reg.agent_sharing_links?.slug
  const invitation_link = slug != null && slug !== '' ? String(slug) : 'Direct'

  return {
    name,
    email,
    phone,
    registered_date,
    status,
    payment,
    account,
    invitation_link
  }
}

/** Available export column keys and their display labels */
export const EXPORT_COLUMN_OPTIONS = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'registered_date', label: 'Registered Date' },
  { key: 'status', label: 'Status' },
  { key: 'payment', label: 'Payment' },
  { key: 'account', label: 'Account' },
  { key: 'invitation_link', label: 'Invitation Link' }
]

/**
 * Build array of export rows (objects with selected columns + custom field keys).
 * @param {object[]} registrations
 * @param {string[]} columnKeys
 * @param {Array<{ label: string, value?: string }>} customFields
 * @returns {Record<string, string>[]}
 */
export function buildExportData(registrations, columnKeys, customFields = []) {
  const keys = [...(columnKeys || [])]
  const customLabels = (customFields || []).map((f) => (f && f.label ? String(f.label).trim() : '').replace(/,/g, ' ') || 'Custom')
  const customValues = (customFields || []).map((f) => (f && f.value != null ? String(f.value) : ''))

  return registrations.map((reg) => {
    const row = getRegistrationExportRow(reg)
    const out = {}
    keys.forEach((k) => {
      if (row[k] !== undefined) out[k] = row[k]
    })
    customLabels.forEach((label, i) => {
      if (label) out[label] = customValues[i] ?? ''
    })
    return out
  })
}

/**
 * Escape a CSV cell (quotes and wrap in quotes if needed).
 * @param {string} cell
 * @returns {string}
 */
function escapeCsvCell(cell) {
  const s = String(cell ?? '')
  if (s.includes('"') || s.includes(',') || s.includes('\n') || s.includes('\r')) {
    return `"${s.replace(/"/g, '""')}"`
  }
  return s
}

/**
 * Download export as CSV.
 * @param {Record<string, string>[]} rows
 * @param {string} filename - e.g. "registrations.csv"
 */
export function downloadCSV(rows, filename = 'registrations.csv') {
  if (rows.length === 0) {
    const headers = []
    const line = headers.map(escapeCsvCell).join(',')
    const blob = new Blob([line + '\n'], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
    return
  }

  const headers = Object.keys(rows[0])
  const lines = [headers.map(escapeCsvCell).join(',')]
  rows.forEach((row) => {
    lines.push(headers.map((h) => escapeCsvCell(row[h])).join(','))
  })
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * Download export as XLSX.
 * @param {Record<string, string>[]} rows
 * @param {string} filename - e.g. "registrations.xlsx"
 */
export async function downloadXLSX(rows, filename = 'registrations.xlsx') {
  const XLSX = await import('xlsx')
  const ws = XLSX.utils.json_to_sheet(rows.length ? rows : [{}])
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Registrations')
  XLSX.writeFile(wb, filename)
}

/**
 * Download export as PDF (sign-in sheet style: table with columns and rows).
 * @param {Record<string, string>[]} rows
 * @param {string} filename - e.g. "registrations.pdf"
 * @param {string} [title] - e.g. "Event Name - Sign-in"
 */
export async function downloadPDF(rows, filename = 'registrations.pdf', title = 'Registrations') {
  const { jsPDF } = await import('jspdf')
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' })
  const pageW = doc.internal.pageSize.getWidth()
  const margin = 40
  let y = 30
  const fontSize = 10
  doc.setFontSize(14)
  doc.text(title, margin, y)
  y += 24

  if (rows.length === 0) {
    doc.setFontSize(fontSize)
    doc.text('No registrations to display.', margin, y)
    doc.save(filename)
    return
  }

  const headers = Object.keys(rows[0])
  const colCount = headers.length
  const colWidth = Math.max(60, (pageW - margin * 2) / colCount)
  const rowHeight = 18

  doc.setFontSize(fontSize)
  doc.setFont(undefined, 'bold')
  headers.forEach((h, i) => {
    doc.text(String(h).slice(0, 20), margin + i * colWidth, y)
  })
  y += rowHeight
  doc.setFont(undefined, 'normal')

  const maxY = doc.internal.pageSize.getHeight() - 40
  rows.forEach((row) => {
    if (y > maxY) {
      doc.addPage([doc.internal.pageSize.getHeight(), pageW], 'landscape')
      y = 30
    }
    headers.forEach((h, i) => {
      doc.text(String(row[h] ?? '').slice(0, 24), margin + i * colWidth, y)
    })
    y += rowHeight
  })

  doc.save(filename)
}
